import BigButton from "components/atoms/buttons/BigButton";
import { FiCreditCard, FiHardDrive, FiLock, FiMonitor } from "react-icons/fi";
import * as s from "../UsageSignupStyled";
import Input from "components/atoms/input/Input";
import { IoBagCheck } from "react-icons/io5";
import {
  formatPrice,
  getPrepaidTotal,
  getYearDiscount,
  getYearlyChannelPrice,
  isYearlyBillingCycle,
} from "utils/priceUtils";
import { UsageSignupFlow } from "../useUsageSignupFlow";

interface Props {
  flow: UsageSignupFlow;
  onAddPayment: () => void;
}

export default function ReviewPurchaseStep(props: Props): JSX.Element {
  const { flow, onAddPayment } = props;
  const { paymentData, paymentPrice, isUpgrade, termYears } = flow.state;
  const isYearly = isYearlyBillingCycle(paymentData.cycle);
  const channelCount = paymentData.channels;
  const storageAmount = paymentData.storage;
  const yearlyChannelPrice = getYearlyChannelPrice(
    paymentPrice.channelPrice,
    getYearDiscount(paymentPrice.year, termYears)
  );
  const channelUnitPrice = isYearly
    ? yearlyChannelPrice
    : paymentPrice.channelPrice;
  const channelTotal = isYearly
    ? getPrepaidTotal(channelCount, channelUnitPrice, termYears)
    : channelCount * channelUnitPrice;
  const storageTotal = isYearly
    ? getPrepaidTotal(storageAmount, paymentPrice.storagePrice, termYears)
    : storageAmount * paymentPrice.storagePrice;
  const total = isYearly ? channelTotal + storageTotal : 0;
  const formattedChannelPrice = formatPrice(channelUnitPrice);
  const termSuffix = isYearly && termYears > 1 ? ` x ${termYears} years` : "";

  return (
    <>
      <s.CenterStage>
        <div className="icon review">
          <IoBagCheck />
        </div>
        <h2>Review Your Order</h2>
        <p>Confirm the details below before proceeding to secure checkout.</p>
      </s.CenterStage>

      <s.ReviewCard>
        <div className="review-header">
          <span>{isYearly ? "Yearly" : "Monthly"}</span>
          <strong>
            {isYearly
              ? `Prepaid — one-time invoice${
                  termYears > 1 ? ` (${termYears}-year term)` : ""
                }`
              : "Usage based"}
          </strong>
        </div>
        <div className="divider" />
        <div className="line-items">
          <div className="line-item">
            <div className="item-main">
              <div className="item-icon">
                <FiMonitor />
              </div>
              <div className="item-copy">
                <h3>Channel Monitoring</h3>

                <p className="meta">
                  {isYearly
                    ? `${channelCount} channels x $${formattedChannelPrice}/yr${termSuffix}`
                    : `$${formattedChannelPrice}/channel/mo`}
                </p>
              </div>
            </div>
            {isYearly && (
              <div className="item-price">
                <strong>${formatPrice(channelTotal)}</strong>
              </div>
            )}
          </div>

          {isYearly && storageAmount > 0 && (
            <div className="line-item">
              <div className="item-main">
                <div className="item-icon">
                  <FiHardDrive />
                </div>
                <div className="item-copy">
                  <h3>Cloud Storage</h3>

                  <p className="meta">
                    {storageAmount} TB x $
                    {formatPrice(paymentPrice.storagePrice)}
                    /yr
                    {termSuffix}
                  </p>
                </div>
              </div>
              <div className="item-price">
                <strong>${formatPrice(storageTotal)}</strong>
              </div>
            </div>
          )}
        </div>

        <div className="divider" />

        <div className="due-today">
          <span>Due today</span>
          <div className="due-today-right">
            <strong>${formatPrice(total)}</strong>
            {!isYearly && (
              <p className="due-today-note">
                + usage-based channel billing at end of period
              </p>
            )}
          </div>
        </div>
      </s.ReviewCard>

      <s.ReviewNotice>
        <FiLock />
        <span>
          {isYearly
            ? "You will be billed based on channel usage and cloud storage owned at end of the period"
            : "This covers usage for channels equal to the number credits being purchased. Any additional channels and/or storage will be billed monthly."}
        </span>
      </s.ReviewNotice>

      <s.FieldGroup>
        <label htmlFor="billing-email">
          Billing Email<span>*</span>
        </label>
        <Input
          id="billing-email"
          value={paymentData.userEmail}
          onChange={(e) => flow.actions.onChangeBillingEmail(e.target.value)}
          // disabled={variant === "upgrade"}
        />
        <p>Invoices and receipts will be sent to this address.</p>
      </s.FieldGroup>

      <s.ButtonRow>
        <BigButton
          label="Back"
          buttonType="cancel"
          onClickBtn={() => flow.actions.onChangePage("previous")}
        />
        <BigButton
          label={isUpgrade ? "Checkout" : "Add Payment Method"}
          onClickBtn={onAddPayment}
          iconComponent={<FiCreditCard />}
        />
      </s.ButtonRow>
    </>
  );
}
